import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
  useNodes,
  useEdges,
} from "@xyflow/react";
import {
  FileText,
  Trash2,
  Settings,
  X,
  AlertTriangle,
  Loader2,
  CheckCircle,
  MessageSquare,
  Files,
} from "lucide-react";
import { AvailableVariable } from "../../common/ui/types"; // Added import
import { formatDragVariable } from "../../utils/variableHelper"; // Added import
import { getAllAncestorNodes } from "../../utils/workflowHelpers"; // Added import
import { getNodeValidationStatus } from "../../utils/nodeValidation";
import syntaxHighlight from "../../utils/jsonViewer";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { PromptEditorWithVariablesModal } from "../../common/ui/PromptEditorWithVariablesModal";
import NodeDocsButton from "../../docs/NodeDocsButton";

// Removed local AvailableVariable interface

export const SummarizeNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [prompt, setPrompt] = useState<string>(data?.prompt || "");
  const [multipleFiles, setMultipleFiles] = useState<string>(
    data?.multiple_files || "",
  );
  const [documentStoreId, setDocumentStoreId] = useState<string>(
    data?.document_store_id || "",
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [, setDraggedVariable] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const { setNodes, deleteElements } = useReactFlow(); // Modified destructuring
  const nodes = useNodes(); // Added
  const edges = useEdges(); // Added

  const executionStatus = data?.executionStatus || null; // 'running', 'success', 'error'
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const validationStatus = useMemo(() => {
    const tempNode: Node<any> = {
      id,
      type: "SUMMARY",
      position: { x: 0, y: 0 },
      data: {
        ...data,
        prompt: prompt,
        multiple_files: multipleFiles,
        document_store_id: documentStoreId,
      },
    };
    return getNodeValidationStatus(tempNode, edges, nodes); // Modified arguments
  }, [
    id,
    data,
    prompt,
    multipleFiles,
    documentStoreId,
    edges,
    nodes,
  ]); // Modified dependencies

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => e.stopPropagation(),
    [],
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object") {
      return { inputs: {}, outputs: {} };
    }

    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs") inputs[key] = value;
    });

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const ancestorNodes = getAllAncestorNodes(id, nodes, edges); // Modified logic
    return ancestorNodes.map((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      return {
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, nodes, edges, extractNodeData]); // Modified dependencies

  // Auto-populate removed as per request
  // useEffect(() => {
  //   if (!multipleFiles && previousNodes.length > 0) {
  //     const lastNode = previousNodes[previousNodes.length - 1];
  //     if (lastNode) {
  //       setMultipleFiles(`${lastNode.nodeName}.outputs`);
  //     }
  //   }
  // }, [previousNodes, multipleFiles]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output",
  ) => {
    e.stopPropagation();
    const fullVariable = formatDragVariable(nodeName, variable, type); // Updated to use formatDragVariable
    setDraggedVariable(fullVariable);
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");

    if (variable) {
      // Check if dropping into multiple_files field
      const targetId = (e.target as HTMLElement).id;
      if (targetId === "multiple_files_drop_zone") {
        setMultipleFiles(variable);
      } else if (targetId === "document_store_id_drop_zone") {
        setDocumentStoreId(variable);
      } else {
        setPrompt((prev) => prev + `{{${variable}}}`);
      }
    }
    setDraggedVariable(null);
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const statusConfig = useMemo(() => {
    const hasConfig = !!prompt;
    return hasConfig
      ? { bg: "bg-indigo-500", text: "Configured" }
      : { bg: "bg-gray-400", text: "Not Configured" };
  }, [prompt]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                prompt: prompt,
                multiple_files: multipleFiles,
                document_store_id: documentStoreId,
              },
            }
          : node,
      ),
    );
  }, [
    prompt,
    multipleFiles,
    documentStoreId,
    id,
    setNodes,
  ]);

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <div
      className={`relative group bg-indigo-50/80 backdrop-blur-xl border-2 rounded-xl p-4 min-w-[280px] max-w-[320px] shadow-sm transition-all ${
        !validationStatus.isValid
          ? "border-red-500 shadow-red-200"
          : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
              ? "border-green-500 shadow-md shadow-green-200"
              : executionError
                ? "border-red-500 shadow-md shadow-red-200"
                : selected
                  ? "border-indigo-500 shadow-md"
                  : "border-indigo-300 hover:border-indigo-400"
      }`}
    >
      {/* Execution Spinner */}
      {isExecuting && (
        <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
          <div className="bg-white rounded-full p-3 shadow-lg">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        </div>
      )}

      {/* Success Overlay */}
      {executionSuccess && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      {/* Error Overlay */}
      {executionError && (
        <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
          <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
        </div>
      )}

      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-indigo-500 border-2 border-white"
        style={{
          boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.25)",
        }}
      />

      <div className="flex items-start justify-between mb-3 cursor-move">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg shadow-sm bg-indigo-100">
            <FileText className="w-4 h-4 text-indigo-700" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800 text-sm">Summarize node</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`w-1.5 h-1.5 rounded-full ${statusConfig.bg}`} />
              <p className="text-xs text-gray-600">{statusConfig.text}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NodeDocsButton nodeType="SUMMARY" />
          <button
            onClick={handleDeleteClick}
            className="text-gray-500 hover:text-gray-700 p-1 rounded transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Prompt preview */}
      <div
        className="p-2.5 bg-white/80 border border-indigo-200 rounded-lg text-xs shadow-inner"
        onClick={stopPropagation}
      >
        {prompt ? (
          <div className="text-gray-600 line-clamp-2 break-words">{prompt}</div>
        ) : (
          <div className="text-gray-400 italic">No prompt configured</div>
        )}
      </div>

      <div className="mt-3" onClick={stopPropagation}>
        <button
          onClick={handleOpenModal}
          className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-xs font-medium flex justify-center gap-2 shadow-sm hover:bg-indigo-700 transition-colors"
        >
          <Settings className="w-3.5 h-3.5" /> Configure Summarize node
        </button>
      </div>

      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-indigo-500 border-2 border-white"
        style={{
          boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.25)",
        }}
      />

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded">
                    <FileText className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure Summarize node
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Write your prompt — model & API key come from AI settings
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left Sidebar - Previous Nodes */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* Center - Main Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6">
                  {!validationStatus.isValid && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-900 mb-2">
                            Configuration Issues
                          </h4>
                          <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                            {validationStatus.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (prompt) {
                        handleCloseModal();
                      }
                    }}
                  >
                    <div className="space-y-5">
                      {/* Model & key are resolved server-side from AI settings */}
                      <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                        <Settings className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-indigo-700">
                          The LLM model and API key are taken from your workspace AI
                          settings automatically — just write the prompt below.
                        </p>
                      </div>

                      {/* Prompt Field - Click to open editor */}
                      <div>
                        <label
                          htmlFor="prompt"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Prompt <span className="text-red-500">*</span>
                        </label>
                        <div
                          onClick={() => setIsPromptModalOpen(true)}
                          className="w-full min-h-[150px] p-4 border border-gray-300 rounded-lg text-sm text-gray-700 bg-gray-50 hover:bg-white hover:border-indigo-400 cursor-pointer transition-all group relative"
                        >
                          {prompt ? (
                            <div className="whitespace-pre-wrap break-words font-mono text-xs line-clamp-6">
                              {prompt}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                              <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
                              <span className="text-sm font-medium">
                                Click to edit prompt
                              </span>
                              <span className="text-xs mt-1">
                                Drag and drop variables from previous nodes
                              </span>
                            </div>
                          )}
                          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="px-2 py-1 bg-indigo-100 text-indigo-600 rounded text-[10px] font-semibold">
                              Edit
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Click to open the prompt editor with variable
                          drag-and-drop support.
                        </p>
                      </div>

                      {/* Document Store ID Input */}
                      <div>
                        <label
                          htmlFor="document-store-id"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Document Store ID{" "}
                          <span className="text-gray-400 font-normal text-xs">
                            (needed for file search)
                          </span>
                        </label>
                        <div
                          id="document_store_id_drop_zone"
                          onDrop={(e) => handleDrop(e)}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`min-h-[60px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                            isDraggingOver
                              ? "border-indigo-400 bg-indigo-50"
                              : documentStoreId
                                ? "border-indigo-300 bg-indigo-50/50"
                                : "border-gray-300 bg-gray-50 hover:border-gray-400"
                          }`}
                        >
                          {documentStoreId ? (
                            <div className="flex items-start justify-between gap-3 pointer-events-none">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-500 mb-1">
                                  Selected source
                                </div>
                                <code className="text-xs text-indigo-700 font-mono break-all">
                                  {documentStoreId}
                                </code>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDocumentStoreId("");
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0 pointer-events-auto"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center text-gray-400 pointer-events-none">
                              <div className="w-5 h-5 mb-1 bg-indigo-100 rounded-full flex items-center justify-center">
                                <span className="text-[10px] text-indigo-600 font-bold">
                                  ID
                                </span>
                              </div>
                              <span className="text-sm">
                                Drag and drop document store ID here
                              </span>
                              <span className="text-xs mt-0.5">
                                e.g., DocumentStore.outputs.id
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Multiple Files Input */}
                      <div>
                        <label
                          htmlFor="multiple-files"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Multiple Files Source{" "}
                          <span className="text-gray-400 font-normal text-xs">
                            (Optional)
                          </span>
                        </label>
                        <div
                          id="multiple_files_drop_zone"
                          onDrop={(e) => handleDrop(e)}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          className={`min-h-[60px] px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-all ${
                            isDraggingOver
                              ? "border-indigo-400 bg-indigo-50"
                              : multipleFiles
                                ? "border-indigo-300 bg-indigo-50/50"
                                : "border-gray-300 bg-gray-50 hover:border-gray-400"
                          }`}
                        >
                          {multipleFiles ? (
                            <div className="flex items-start justify-between gap-3 pointer-events-none">
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-gray-500 mb-1">
                                  Selected source
                                </div>
                                <code className="text-xs text-indigo-700 font-mono break-all">
                                  {multipleFiles}
                                </code>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMultipleFiles("");
                                }}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0 pointer-events-auto"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center text-gray-400 pointer-events-none">
                              <Files className="w-5 h-5 mb-1 text-gray-300" />
                              <span className="text-sm">
                                Drag and drop files source here
                              </span>
                              <span className="text-xs mt-0.5">
                                e.g., PreviousNode.outputs
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!prompt}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save Configuration
                      </button>
                    </div>
                  </form>
                </div>

                {/* Right Sidebar - Outputs */}
                <div className="w-80 bg-gray-50 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Node Outputs
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Available after execution
                  </p>

                  {hasOutputs && (
                    <div className="mt-4 pt-3 border-t border-indigo-200">
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        JSON Output
                      </h4>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        <pre className="whitespace-pre-wrap break-words">
                          <code
                            dangerouslySetInnerHTML={{
                              __html: syntaxHighlight(data.outputs),
                            }}
                          />
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Prompt Editor Modal with Variables */}
      <PromptEditorWithVariablesModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        value={prompt}
        onChange={setPrompt}
        title="Edit Summarize Prompt"
        description="Write your prompt and drag variables from previous nodes"
        placeholder="Enter your prompt here...

Examples:
- Summarize the following document: {{PreviousNode.outputs.content}}
- Extract key points from: {{OCRNode.outputs.text}}
- Analyze and provide insights on the data"
        availableVariables={previousNodes}
      />
    </div>
  );
};

export default SummarizeNode;
